import React from 'react';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Checkbox } from 'antd';
import { CheckboxChangeEventTarget } from 'antd/lib/checkbox/Checkbox';
import upperCase from 'lodash/upperCase';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import {
  IDetails,
  ICondoGuests,
  ICondoUnit,
  CondoMatchTypeEnum,
  ICondoUnits,
  DateSearchTypeEnum,
} from 'src/common-types/index';
import { BlueButton, CondoGuestsPickerComponent } from 'src/components/components/index';
import { getDateCheckInOutISOString, UrlUtils } from 'src/utils/index';
import {
  IUnitsSearchState,
  unitsSearchActions,
  getUnitsDetails,
  getCheckinStatus,
  IMenuState,
} from 'src/store/slices/index';
import { RootState } from 'src/store';
import {
  CalendarNotificationSvg,
  ErrorAlertSvg,
  UpdateSvg,
  YellowConfirmationSvg,
} from 'src/assets/index';
import {
  Routes,
  CONDO_GUESTS_LABEL,
  CONDO_DATES_LABEL,
  CONDO_UNITS_SEARCH_LABEL,
  CONDO_IS_FLEXIBLE_LABEL,
} from 'src/constants/index';
import { UnitSkeleton } from '../unit-skeleton';
import { AlternativeDatesSkeleton } from '../alternative-dates-skeleton';
import { Unit } from '../condo-unit';
import { ListUnits } from '../condo-list-units';
import { CondoStrictDatePickerComponentWithIntl } from '../../../condo-search/condo-strict-date-picker';
import './style.scss';

interface IMapStateToProps {
  unitsSearchStore: IUnitsSearchState;
  menuStore: IMenuState;
}

interface IMapDispatchToProps {
  clearError: () => void;
  setDates: (dates: { startDate: Date; endDate: Date }) => void;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  setKey: (key: string) => void;
  updateAdultsCount: (count: number) => void;
  updateKidsCount: (count: number) => void;
  updateBedroomCount: (count: number) => void;
  setPetsAllowed: (allowed: boolean) => void;
  setDatesString: (dates: { startDate: string; endDate: string }) => void;
  setGuestsCount: (guests: ICondoGuests) => void;
  getUnitsDetails: (condoId: number) => void;
  resetDates: () => void;
  getCheckinStatus: (start: string, end: string) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps {
  condoDetails: IDetails;
  refAnchor: React.RefObject<HTMLDivElement>;
  matches?: boolean;
  isShortStaysSupported?: boolean;
}

interface IState {
  showOnlyAvailableUnits: boolean;
  notificationDateRange: { startDate: string; endDate: string };
  currentUnitName: string;
  firstUnitName: string;
  isUnitsUpdate: boolean;
}

const ZERO = 0;
const ONE = 1;

class UnitsSearchComponent extends React.Component<IProps, IState> {
  state: IState = {
    showOnlyAvailableUnits: false,
    notificationDateRange: null,
    currentUnitName: '',
    firstUnitName: '',
    isUnitsUpdate: false,
  };

  getUnits = (matchType: string) => {
    const { condoDetails, unitsSearchStore } = this.props;
    const { showOnlyAvailableUnits } = this.state;
    const units: ICondoUnit[] = [];

    const getUnits =
      (unitsSearchStore.isSearchUnits && unitsSearchStore.condoInfo) ||
      (isEmpty(condoDetails.units) && !isUndefined(unitsSearchStore.condoInfo?.units))
        ? unitsSearchStore.condoInfo?.units
        : condoDetails.units;

    const condoUnits =
      showOnlyAvailableUnits && matchType === CondoMatchTypeEnum.Alternative
        ? getUnits.filter((item) =>
            item.availibilities.some((value) => value.matchType === CondoMatchTypeEnum.Exact),
          )
        : getUnits;

    condoUnits.forEach((unit) => {
      unit.availibilities.forEach((item) => {
        if (item.matchType === matchType) {
          units.push({
            id: unit.id,
            name: unit.name
              ? unit.name
              : `${upperCase(unit.kitchenType)}-${unit.bedroomsCount}/${unit.maxOccupancy}/${
                  unit.privacyOccupancy
                }`,
            maxOccupancy: unit.maxOccupancy,
            privacyOccupancy: unit.privacyOccupancy,
            bedroomsCount: unit.bedroomsCount,
            bathroomsCount: unit.bathroomsCount,
            kitchenType: unit.kitchenType,
            unitId: item.id,
            price: item.price,
            pricePerNight: item.pricePerNight,
            publicPrice: item?.publicPrice,
            dateRange: {
              from: item.dateRange.from,
              to: item.dateRange.to,
            },
            matchType: item.matchType,
            sleepingArrangements: unit.sleepingArrangements,
            bathroom: unit.bathroom,
            cancellationPolicyText: item.cancellationPolicyText,
            refundability: item.refundability,
            bedRoomType: unit.bedRoomType,
            nonSmokingRoomsByRequest: unit.nonSmokingRoomsByRequest,
            loft: unit.loft,
            unitConfig: unit.unitConfig,
            outdoorLivingSpaceInfo: unit.outdoorLivingSpaceInfo,
            bathroomDescription: unit.bathroomDescription,
            bedroomBathLocation: unit.bedroomBathLocation,
            rollInShower: unit.rollInShower,
            bathroomAccessible: unit.bathroomAccessible,
            kitchenEquipments: unit.kitchenEquipments,
            babyFacilities: unit.babyFacilities,
            roomAmenities: unit.roomAmenities,
            roomFurnishing: unit.roomFurnishing,
            televisionServiceType: unit.televisionServiceType,
            televisionType: unit.televisionType,
            numberOfTVs: unit.numberOfTVs,
            televisionLocations: unit.televisionLocations,
            coolingSystems: unit.coolingSystems,
            accessibility: unit.accessibility,
            safebox: unit.safebox,
            safetyEquipment: unit.safetyEquipment,
            housekeeping: unit.housekeeping,
            housekeepingFeeDescription: unit.housekeepingFeeDescription,
            supplierIdentifier: unit.supplierIdentifier,
          });
        }
      });
    });

    return units;
  };

  updateSearch = () => {
    const { getUnitsDetails, condoDetails, unitsSearchStore } = this.props;

    if (unitsSearchStore.startDate && unitsSearchStore.endDate) {
      getUnitsDetails(condoDetails.id);
      this.setState({
        isUnitsUpdate: true,
        notificationDateRange: {
          startDate: unitsSearchStore.startDate,
          endDate: unitsSearchStore.endDate,
        },
      });
    }
  };

  onToggleShowOnlyAvailableUnits = (e: { target: CheckboxChangeEventTarget }): void => {
    this.setState({
      showOnlyAvailableUnits: e.target.checked,
      currentUnitName: this.state.firstUnitName,
    });
  };

  getOnlyAlternativeUnits = (matchType: string) => {
    const { condoDetails, unitsSearchStore } = this.props;
    const condoUnits =
      (unitsSearchStore.isSearchUnits && unitsSearchStore.condoInfo) ||
      (isEmpty(condoDetails.units) && !isUndefined(unitsSearchStore.condoInfo?.units))
        ? unitsSearchStore.condoInfo?.units
        : condoDetails.units;
    const onlyAlternativeUnits = condoUnits.filter((unit) => {
      return unit.availibilities.every((item) => item.matchType === matchType);
    });

    return onlyAlternativeUnits.map((unit: ICondoUnits) => {
      return {
        id: unit.id,
        name: unit.name
          ? unit.name
          : `${upperCase(unit.kitchenType)}-${unit.bedroomsCount}/${unit.maxOccupancy}/${
              unit.privacyOccupancy
            }`,
        maxOccupancy: unit.maxOccupancy,
        privacyOccupancy: unit.privacyOccupancy,
        bedroomsCount: unit.bedroomsCount,
        bathroomsCount: unit.bathroomsCount,
        kitchenType: unit.kitchenType,
        unitId: unit.id,
        price: ZERO,
        pricePerNight: ZERO,
        publicPrice: ZERO,
        dateRange: {
          from: '',
          to: '',
        },
        matchType: matchType,
        sleepingArrangements: unit.sleepingArrangements,
        bathroom: unit.bathroom,
        cancellationPolicyText: '',
        refundability: '',
        bedRoomType: unit.bedRoomType,
        nonSmokingRoomsByRequest: unit.nonSmokingRoomsByRequest,
        loft: unit.loft,
        unitConfig: unit.unitConfig,
        outdoorLivingSpaceInfo: unit.outdoorLivingSpaceInfo,
        bathroomDescription: unit.bathroomDescription,
        bedroomBathLocation: unit.bedroomBathLocation,
        rollInShower: unit.rollInShower,
        bathroomAccessible: unit.bathroomAccessible,
        kitchenEquipments: unit.kitchenEquipments,
        babyFacilities: unit.babyFacilities,
        roomAmenities: unit.roomAmenities,
        roomFurnishing: unit.roomFurnishing,
        televisionServiceType: unit.televisionServiceType,
        televisionType: unit.televisionType,
        numberOfTVs: unit.numberOfTVs,
        televisionLocations: unit.televisionLocations,
        coolingSystems: unit.coolingSystems,
        accessibility: unit.accessibility,
        safebox: unit.safebox,
        safetyEquipment: unit.safetyEquipment,
        housekeeping: unit.housekeeping,
        housekeepingFeeDescription: unit.housekeepingFeeDescription,
        supplierIdentifier: unit.supplierIdentifier,
      };
    });
  };

  getAlternativeUnits = () => {
    const { condoDetails, unitsSearchStore } = this.props;
    const condoUnits =
      (unitsSearchStore.isSearchUnits && unitsSearchStore.condoInfo) ||
      (isEmpty(condoDetails.units) && !isUndefined(unitsSearchStore.condoInfo?.units))
        ? unitsSearchStore.condoInfo?.units
        : condoDetails.units;

    return condoUnits.map((unit: ICondoUnits) => {
      return {
        id: unit.id,
        name: unit.name
          ? unit.name
          : `${upperCase(unit.kitchenType)}-${unit.bedroomsCount}/${unit.maxOccupancy}/${
              unit.privacyOccupancy
            }`,
        maxOccupancy: unit.maxOccupancy,
        privacyOccupancy: unit.privacyOccupancy,
        bedroomsCount: unit.bedroomsCount,
        bathroomsCount: unit.bathroomsCount,
        kitchenType: unit.kitchenType,
        unitId: unit.id,
        price: ZERO,
        pricePerNight: ZERO,
        publicPrice: ZERO,
        dateRange: {
          from: '',
          to: '',
        },
        matchType: CondoMatchTypeEnum.Alternative,
        sleepingArrangements: unit.sleepingArrangements,
        bathroom: unit.bathroom,
        cancellationPolicyText: '',
        refundability: '',
        bedRoomType: unit.bedRoomType,
        nonSmokingRoomsByRequest: unit.nonSmokingRoomsByRequest,
        loft: unit.loft,
        unitConfig: unit.unitConfig,
        outdoorLivingSpaceInfo: unit.outdoorLivingSpaceInfo,
        bathroomDescription: unit.bathroomDescription,
        bedroomBathLocation: unit.bedroomBathLocation,
        rollInShower: unit.rollInShower,
        bathroomAccessible: unit.bathroomAccessible,
        kitchenEquipments: unit.kitchenEquipments,
        babyFacilities: unit.babyFacilities,
        roomAmenities: unit.roomAmenities,
        roomFurnishing: unit.roomFurnishing,
        televisionServiceType: unit.televisionServiceType,
        televisionType: unit.televisionType,
        numberOfTVs: unit.numberOfTVs,
        televisionLocations: unit.televisionLocations,
        coolingSystems: unit.coolingSystems,
        accessibility: unit.accessibility,
        safebox: unit.safebox,
        safetyEquipment: unit.safetyEquipment,
        housekeeping: unit.housekeeping,
        housekeepingFeeDescription: unit.housekeepingFeeDescription,
        supplierIdentifier: unit.supplierIdentifier,
      };
    });
  };

  getCurrentUnit = (value: string, index: number): void => {
    this.setState({ currentUnitName: value });

    if (index === ZERO) {
      this.setState({ firstUnitName: value });
    }
  };

  getNoAvailability = (menuStore: IMenuState) => {
    return (
      <div className="units-search__no-availability-banner">
        <div className="units-search__banner-icon">
          <ErrorAlertSvg />
        </div>
        <div className="units-search__no-availability-text-wrapper">
          <div className="units-search__no-availability-header">
            <p className="units-search__no-availability-text-help-you-find">
              <FormattedMessage id="this.is.a.high.demand.time.and.location" />
              <span>
                <FormattedMessage id="request.a.custom.search" />
                {menuStore.items?.baseUrl ? (
                  <a
                    href={`${Routes.CondoSearch}/submit-request`}
                    className="units-search__no-availability-support-link"
                  >
                    <FormattedMessage id="click.this.link" />
                  </a>
                ) : null}
              </span>
              <span>
                <FormattedMessage id="or.call.your" />
                <a
                  href={`tel:${menuStore.items?.customerServiceNumber}`}
                  className="units-search__no-availability-support-link"
                >
                  {menuStore.items?.customerServiceNumber}
                </a>
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  componentDidMount() {
    const { setDatesString, setGuestsCount } = this.props;
    const values = UrlUtils.getValues();
    const isFlexibleSearch = values[CONDO_IS_FLEXIBLE_LABEL] === '1';

    if (!values[CONDO_IS_FLEXIBLE_LABEL]) {
      UrlUtils.setUrl(CONDO_IS_FLEXIBLE_LABEL, ONE);
    }

    if (values[CONDO_UNITS_SEARCH_LABEL]) {
      const { startDate, endDate, guests } = values[CONDO_UNITS_SEARCH_LABEL] as {
        startDate: string;
        endDate: string;
        guests: ICondoGuests;
      };

      if (!isFlexibleSearch) {
        setDatesString({ startDate, endDate } as { startDate: string; endDate: string });
        this.setState({
          notificationDateRange: values[CONDO_UNITS_SEARCH_LABEL] as {
            startDate: string;
            endDate: string;
          },
        });
      }

      setGuestsCount(guests);
    } else {
      if (values[CONDO_DATES_LABEL]) {
        if (!isFlexibleSearch) {
          setDatesString(values[CONDO_DATES_LABEL] as { startDate: string; endDate: string });
          this.setState({
            notificationDateRange: values[CONDO_DATES_LABEL] as {
              startDate: string;
              endDate: string;
            },
          });
        }
      }

      if (values[CONDO_GUESTS_LABEL]) {
        setGuestsCount(values[CONDO_GUESTS_LABEL] as ICondoGuests);
      }
    }
  }

  render(): React.ReactNode {
    const {
      refAnchor,
      condoDetails,
      unitsSearchStore,
      matches,
      updateAdultsCount,
      updateKidsCount,
      updateBedroomCount,
      setPetsAllowed,
      setDates,
      setStartDate,
      setEndDate,
      setKey,
      resetDates,
      getCheckinStatus,
      isShortStaysSupported,
      menuStore,
    } = this.props;
    const { showOnlyAvailableUnits, notificationDateRange, currentUnitName, isUnitsUpdate } =
      this.state;
    const { endDate, startDate, guests, isUnitsLoading, key, error } = unitsSearchStore;
    const isOnlyAlternative = this.getUnits(CondoMatchTypeEnum.Exact).length === ZERO;
    const isOnlyExact =
      this.getOnlyAlternativeUnits(CondoMatchTypeEnum.Alternative).length === ZERO;
    const showAlternativeMessage =
      condoDetails.units.length && notificationDateRange && isOnlyAlternative;

    return (
      <div className="units-search" ref={refAnchor}>
        <h2 className="units-search__header">
          <FormattedMessage id="availability.title" values={{ name: 'Units' }} />
        </h2>
        <div className="units-search__search">
          <CondoStrictDatePickerComponentWithIntl
            getCheckinStatus={getCheckinStatus}
            isShortStaysSupported={isShortStaysSupported}
            matches={matches}
            isFromDetails={true}
            isSearch={false}
            condoStrictDatesStore={{
              startDate: startDate,
              endDate: endDate,
              key: key,
              error: '',
              searchType: DateSearchTypeEnum.Strict,
            }}
            setSearchType={() => void ZERO}
            resetFlexibleState={() => void ZERO}
            clearError={() => void ZERO}
            setKey={setKey}
            setDates={setDates}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            resetDates={resetDates}
          />
          <CondoGuestsPickerComponent
            updateAdultsCount={updateAdultsCount}
            updateKidsCount={updateKidsCount}
            updateBedroomCount={updateBedroomCount}
            setPetsAllowed={setPetsAllowed}
            condoGuestsStore={guests}
          />
          <div className="units-search__button">
            <BlueButton onClick={this.updateSearch}>
              <UpdateSvg />
              <FormattedMessage id="update.search.your.hotel" />
            </BlueButton>
          </div>
        </div>
        <div className="units-search__list">
          {isUnitsLoading ? (
            <>
              <div className="units-search__list-skeleton">
                <UnitSkeleton />
                <UnitSkeleton />
                <UnitSkeleton />
                <UnitSkeleton />
              </div>
              <AlternativeDatesSkeleton />
            </>
          ) : (
            <>
              {error.length === ZERO &&
              !showAlternativeMessage &&
              (startDate || endDate) !== undefined ? (
                <div className="units-search__default-banner">
                  <div className="units-search__banner-icon">
                    <YellowConfirmationSvg />
                  </div>
                  <div className="units-search__default-banner-text-wrapper">
                    <div className="units-search__default-banner-header">
                      <FormattedMessage id="majority.of.resorts.assign.specific.unit" />
                    </div>
                    <div className="units-search__default-banner-text">
                      <FormattedMessage id="please.check.available.unit" />
                    </div>
                  </div>
                </div>
              ) : null}
              {error.length === ZERO && showAlternativeMessage
                ? this.getNoAvailability(menuStore)
                : null}
              {(startDate || endDate) === undefined ? (
                <div className="units-search__specify-travel-banner">
                  <div className="units-search__banner-icon">
                    <CalendarNotificationSvg />
                  </div>
                  <div className="units-search__specify-travel-text-wrapper">
                    <div className="units-search__specify-travel-text">
                      <FormattedMessage id="specify.travel.dates" />
                    </div>
                  </div>
                </div>
              ) : null}
              <h3 className="units-search__subheading">
                {!isOnlyAlternative ? <FormattedMessage id="unit.types" /> : null}
              </h3>
              <div className="units-search__cards">
                {error.length === ZERO && !isOnlyExact && !isOnlyAlternative ? (
                  <Checkbox
                    onChange={this.onToggleShowOnlyAvailableUnits}
                    checked={showOnlyAvailableUnits}
                  >
                    <FormattedMessage id="show.only.available.units" />
                  </Checkbox>
                ) : null}
                {error.length === ZERO &&
                ((!isUnitsUpdate && !!condoDetails.units.length) ||
                  !!unitsSearchStore.condoInfo?.units.length) ? (
                  <>
                    {notificationDateRange && isOnlyAlternative ? (
                      <div className="units-search__cards-wrapper">
                        {this.getAlternativeUnits().map((unit, index) => (
                          <Unit
                            key={index}
                            unit={unit}
                            condo={condoDetails}
                            condoId={condoDetails.id}
                            matchType={unit.matchType}
                            isOnlyAlternative={true}
                            isAnytime={isNull(notificationDateRange)}
                            getCurrentUnit={this.getCurrentUnit}
                            currentUnitName={currentUnitName}
                            index={index}
                            isEmptyAlternativeUnit={isEmpty(
                              this.getUnits(CondoMatchTypeEnum.Alternative),
                            )}
                            quantityAlternativeUnit={
                              this.getUnits(CondoMatchTypeEnum.Alternative).length
                            }
                            isSpecificDates={(startDate || endDate) === undefined}
                            travelDates={{ startDate, endDate }}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="units-search__cards-wrapper">
                        {(showOnlyAvailableUnits
                          ? this.getUnits(CondoMatchTypeEnum.Exact)
                          : [
                              ...this.getUnits(CondoMatchTypeEnum.Exact),
                              ...this.getOnlyAlternativeUnits(CondoMatchTypeEnum.Alternative),
                            ]
                        ).map((unit, index) => (
                          <Unit
                            key={index}
                            unit={unit}
                            condo={condoDetails}
                            condoId={condoDetails.id}
                            matchType={unit.matchType}
                            isOnlyAlternative={isOnlyAlternative}
                            isAnytime={isNull(notificationDateRange)}
                            getCurrentUnit={this.getCurrentUnit}
                            currentUnitName={currentUnitName}
                            index={index}
                            isEmptyAlternativeUnit={
                              !isEmpty(this.getUnits(CondoMatchTypeEnum.Alternative))
                            }
                            quantityAlternativeUnit={
                              this.getUnits(CondoMatchTypeEnum.Alternative).length
                            }
                            isSpecificDates={(startDate || endDate) === undefined}
                            travelDates={{ startDate, endDate }}
                          />
                        ))}
                      </div>
                    )}
                  </>
                ) : error.length !== ZERO ? (
                  this.getNoAvailability(menuStore)
                ) : null}
              </div>
              {error.length === ZERO ? (
                <div className="units-search__list-wrapper">
                  {!isEmpty(this.getUnits(CondoMatchTypeEnum.Alternative)) && (
                    <ListUnits
                      title={<FormattedMessage id="alternative.dates" />}
                      alternativeUnits={this.getUnits(CondoMatchTypeEnum.Alternative)}
                      condoId={condoDetails.id}
                      isOnlyAlternative={isOnlyAlternative}
                      currentUnitName={currentUnitName}
                      getCurrentUnit={this.getCurrentUnit}
                      baseUrl={menuStore.items?.baseUrl}
                    />
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    unitsSearchStore: state.unitsSearchStore,
    menuStore: state.navigationMenuStore,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<RootState, undefined, Action>,
): IMapDispatchToProps => ({
  setDates: (dates: { startDate: Date; endDate: Date }) => {
    dispatch(
      unitsSearchActions.setDates({
        startDate: getDateCheckInOutISOString(dates.startDate),
        endDate: getDateCheckInOutISOString(dates.endDate),
      }),
    );
  },
  setDatesString: (dates: { startDate: string; endDate: string }) => {
    dispatch(
      unitsSearchActions.setDates({
        startDate: dates.startDate,
        endDate: dates.endDate,
      }),
    );
  },
  setStartDate: (date: Date) => {
    dispatch(unitsSearchActions.setStartDate(getDateCheckInOutISOString(date)));
  },
  setEndDate: (date: Date) => {
    dispatch(unitsSearchActions.setEndDate(getDateCheckInOutISOString(date)));
  },
  setKey: (key: string) => {
    dispatch(unitsSearchActions.setKey(key));
  },
  clearError: () => {
    dispatch(unitsSearchActions.setError(''));
  },
  updateAdultsCount: (adults: number) => {
    dispatch(unitsSearchActions.updateAdultsCount(adults));
  },
  updateKidsCount: (kids: number) => {
    dispatch(unitsSearchActions.updateKidsCount(kids));
  },
  updateBedroomCount: (bedroom: number) => {
    dispatch(unitsSearchActions.updateBedroomCount(bedroom));
  },
  setPetsAllowed: (petsAllowed: boolean) => {
    dispatch(unitsSearchActions.setPetsAllowed(petsAllowed));
  },
  setGuestsCount: (guests: ICondoGuests) => {
    dispatch(unitsSearchActions.setGuestsCount(guests));
  },
  getCheckinStatus: (start: string, end: string) => {
    dispatch(getCheckinStatus(start, end));
  },
  resetDates: () => {
    dispatch(unitsSearchActions.resetDates());
  },
  getUnitsDetails: (condoId: number) => {
    dispatch(getUnitsDetails(condoId));
  },
});

export const UnitsSearch = connect(mapStateToProps, mapDispatchToProps)(UnitsSearchComponent);

